import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Sparkles, MousePointer2 } from "lucide-react";
import { RevealLines } from "@/components/shared/RevealText";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
    const y2 = useTransform(scrollYProgress, [0, 1], [0, 80]);
    const rotate = useTransform(scrollYProgress, [0, 1], [-6, 0]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

    return (
        <section ref={ref} className="relative overflow-hidden pt-16 pb-24 md:pt-24 md:pb-32" data-testid="hero-section">
            {/* Ambient floating shapes */}
            <motion.div
                aria-hidden
                style={{ y: y1 }}
                className="pointer-events-none absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-pink/10 blur-3xl"
            />
            <motion.div
                aria-hidden
                style={{ y: y2 }}
                className="pointer-events-none absolute -right-40 top-40 h-[520px] w-[520px] rounded-full bg-ink/[0.04] blur-3xl"
            />
            <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[600px] bg-[radial-gradient(1200px_600px_at_50%_-100px,rgba(244,63,94,0.08),transparent)]" />

            <div className="container-x relative">
                {/* Eyebrow row */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.05 }}
                    className="mb-10 flex flex-wrap items-center justify-between gap-4"
                >
                    <div className="flex items-center gap-3">
                        <span className="grid h-6 w-6 place-items-center rounded-full bg-pink">
                            <Sparkles className="h-3 w-3 text-white" />
                        </span>
                        <span className="eyebrow">Concept Prototype · v01</span>
                    </div>
                    <div className="hidden items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-slate-500 md:flex">
                        <span>Telangana</span>
                        <span className="h-1 w-1 rounded-full bg-slate-400" />
                        <span>Est. 2026</span>
                        <span className="h-1 w-1 rounded-full bg-slate-400" />
                        <span>Citizen First</span>
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 items-end gap-16 lg:grid-cols-12 lg:gap-10">
                    {/* Left: kinetic type */}
                    <div className="lg:col-span-8">
                        <h1 className="h-display font-display text-[52px] font-black tracking-tighter text-ink sm:text-[72px] md:text-[104px] lg:text-[132px]">
                            <RevealLines
                                lines={["Meet with"]}
                                delay={0.15}
                                testId="hero-headline-line-1"
                            />
                            <RevealLines
                                lines={[
                                    <span key="k" className="inline-block">
                                        <span className="italic text-pink" style={{ fontFamily: "Playfair Display" }}>
                                            KTR
                                        </span>
                                        <span className="ml-4 inline-block align-middle text-ink/25">.</span>
                                    </span>,
                                ]}
                                delay={0.3}
                                testId="hero-headline-line-2"
                            />
                        </h1>

                        {/* Sub row */}
                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-8 grid grid-cols-1 items-end gap-8 md:grid-cols-12"
                        >
                            <p className="md:col-span-7 max-w-xl text-[16px] leading-relaxed text-slate-600 md:text-[18px]">
                                Connecting citizens, ideas and leadership through one digital platform — designed like
                                a product, priced like a public good.
                            </p>
                            <div className="md:col-span-5 flex flex-wrap items-center gap-3">
                                <Link
                                    to="/request-meeting"
                                    className="btn-primary group"
                                    data-testid="hero-cta-request-meeting"
                                >
                                    Request a Meeting
                                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </Link>
                                <Link
                                    to="/share-ideas"
                                    className="btn-secondary"
                                    data-testid="hero-cta-share-idea"
                                >
                                    Share an Idea
                                </Link>
                            </div>
                        </motion.div>
                    </div>

                    {/* Right: spotlight portrait */}
                    <motion.div
                        style={{ rotate, scale }}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="relative mx-auto w-full max-w-[380px] lg:col-span-4"
                    >
                        <div className="relative aspect-[3/4] overflow-hidden rounded-[28px] border border-ink/10">
                            <img
                                src="/assets/ktr.webp"
                                alt="KTR portrait"
                                className="h-full w-full object-cover object-[center_15%]"
                                loading="eager"
                            />
                            {/* Spotlight */}
                            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(600px_300px_at_60%_20%,rgba(255,255,255,0.15),transparent_60%)]" />
                            {/* Clipping frame */}
                            <div className="pointer-events-none absolute inset-3 border border-white/40" />
                        </div>
                        {/* Floating meta */}
                        <div className="absolute -left-6 top-6 hidden rotate-[-6deg] rounded-full border border-ink/10 bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-ink shadow-sm md:block">
                            Concept · Demo
                        </div>
                        <div className="absolute -bottom-6 right-4 rounded-full bg-ink px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
                            One platform
                        </div>
                    </motion.div>
                </div>

                {/* Bottom row: scroll cue + ticker */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.1, duration: 0.6 }}
                    className="mt-24 flex items-center justify-between border-t border-ink/10 pt-6"
                >
                    <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-slate-500">
                        <MousePointer2 className="h-4 w-4" />
                        Scroll to explore
                    </div>
                    <div className="hidden items-center gap-4 text-[11px] uppercase tracking-[0.24em] text-slate-500 md:flex">
                        <span>01 — Services</span>
                        <span className="h-px w-8 bg-slate-300" />
                        <span>02 — Manifesto</span>
                        <span className="h-px w-8 bg-slate-300" />
                        <span>03 — Track</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
